/** Back office → Catalogue (C2): every service and package, with what is left to complete. */

import {
  type Service,
  type ServicePackage,
  useCatalogPackagesList,
  useCatalogServicesList,
} from "@agencyos/api-client";
import { formatXaf, packageState } from "@agencyos/core";
import { Badge, type BadgeTone, EmptyState, GlassCard, Skeleton, SkillIcon, type Tone } from "@agencyos/ui-web";
import { BookOpenCheck, CircleAlert, CircleCheck, GraduationCap, type LucideIcon, Package, Plane, Plus, Stamp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { useCanEditSettings } from "../lib/settings";

const TONE: Record<ReturnType<typeof packageState>, BadgeTone> = {
  published: "success",
  changes: "offer",
  incomplete: "danger",
  draft: "default",
  archived: "default",
};

function price(pkg: ServicePackage, t: (key: string) => string): string {
  if (pkg.pricing === "free") return t("catalog.pricing.free");
  if (pkg.pricing === "quote") return t("catalog.pricing.quote");
  return pkg.announced_price_xaf != null ? formatXaf(pkg.announced_price_xaf) : "—";
}

/** Icon and tone of a service category (display only: the category itself comes from the data). */
const OTHER: { icon: LucideIcon; tone: Tone } = { icon: Package, tone: "neutral" };
const CATEGORY: Record<string, { icon: LucideIcon; tone: Tone }> = {
  immigration: { icon: Stamp, tone: "accent" },
  studies: { icon: GraduationCap, tone: "success" },
  visa: { icon: Plane, tone: "info" },
  travel: { icon: Plane, tone: "warning" },
  training: { icon: BookOpenCheck, tone: "red" },
  other: { icon: Package, tone: "neutral" },
};

function ServiceCard({ service, packages }: { service: Service; packages: ServicePackage[] }) {
  const category = CATEGORY[service.category] ?? OTHER;
  const CategoryIcon = category.icon;
  const { t, i18n } = useTranslation();
  const canEdit = useCanEditSettings();
  return (
    <GlassCard as="section" className="stack service-card" aria-labelledby={`service-${service.id}`}>
      <div className="service-card__head">
        <SkillIcon size="lg" tone={category.tone} icon={<CategoryIcon size={20} strokeWidth={1.75} />} />
        <div className="service-card__title">
          <h2 id={`service-${service.id}`}>
            <span className="code-chip">{service.code}</span> {localized(service.name, i18n.language)}
          </h2>
          <p className="muted">
            {t(`catalog.category.${service.category}`)} · {t("catalog.packageCount", { count: packages.length })}
          </p>
        </div>
        <div className="row">
          {service.is_active === false ? <Badge>{t("catalog.inactive")}</Badge> : null}
          {canEdit ? (
            <Link className="link" to={`/catalog/services/${service.id}`}>
              {t("catalog.editService")}
            </Link>
          ) : null}
        </div>
      </div>
      {packages.length ? (
        <ul className="plain-list package-list">
          {packages.map((pkg) => {
            const state = packageState(pkg);
            return (
              <li key={pkg.id} className="package-row">
                <Link to={`/catalog/packages/${pkg.id}`} className="package-row__link">
                  <span className="package-row__name">{localized(pkg.name, i18n.language)}</span>
                  <span className="muted">{pkg.code}</span>
                </Link>
                <span className="package-row__price ao-num">{price(pkg, t)}</span>
                <Badge tone={TONE[state]}>{t(`catalog.status.${state}`)}</Badge>
              </li>
            );
          })}
        </ul>
      ) : (
        <EmptyState compact icon={<Package size={22} strokeWidth={1.75} />} title={t("catalog.noPackage")} />
      )}
      {canEdit ? (
        <Link className="ao-button ao-button--tinted ao-button--sm service-card__add" to={`/catalog/packages/new?service=${service.id}`}>
          <Plus size={15} strokeWidth={2} aria-hidden="true" />
          {t("catalog.newPackage")}
        </Link>
      ) : null}
    </GlassCard>
  );
}

export function CatalogPage() {
  const { t } = useTranslation();
  const canEdit = useCanEditSettings();
  const services = useCatalogServicesList();
  const packages = useCatalogPackagesList();

  let content;
  if (services.isPending || packages.isPending)
    content = (
      <GlassCard aria-busy="true">
        <Skeleton />
      </GlassCard>
    );
  else if (services.data?.status !== 200 || packages.data?.status !== 200)
    content = <GlassCard role="alert">{t("common.error")}</GlassCard>;
  else {
    const all = packages.data.data.results;
    const incomplete = all.filter((p) => packageState(p) === "incomplete").length;
    content = (
      <>
        <p role="status" className={`status-banner${incomplete ? " status-banner--warning" : " status-banner--ok"}`}>
          {incomplete ? <CircleAlert size={16} strokeWidth={1.75} aria-hidden="true" /> : <CircleCheck size={16} strokeWidth={1.75} aria-hidden="true" />}
          {incomplete ? t("catalog.toComplete", { count: incomplete }) : t("catalog.allReady")}
        </p>
        <div className="catalog-grid">
          {services.data.data.results.map((service) => (
            <ServiceCard key={service.id} service={service} packages={all.filter((p) => p.service === service.id)} />
          ))}
        </div>
      </>
    );
  }

  return (
    <Page
      title={t("catalog.title")}
      description={
        <>
          {t("catalog.intro")}
          {canEdit ? null : <> {t("common.readOnly")}</>}
        </>
      }
      actions={
        canEdit ? (
          <Link className="ao-button ao-button--primary" to="/catalog/services/new">
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            {t("catalog.newService")}
          </Link>
        ) : null
      }
    >
      {content}
    </Page>
  );
}
